/**
 * Counting reps by the motion sensor in a workout (todo 8): the per-exercise switch "Reps per Sensor zählen", the
 * profiles learned per exercise and placement (`RepProfile`), and what happens to the recordings of the sensor sets
 * when a workout ends.
 *
 * Kept on the phone in one META record (`REP_SENSOR`), outside backups and imports like the recordings: the
 * profiles describe this phone in this pocket, and the switch goes with the phone.
 */
import { buildRecording, type MotionRecording, type Placement, type RawRecording, type RecordingInfo, type StoredRecording } from "./motion";
import { learnProfile, type RepProfile } from "./reps";
import { META, newId, RECORDINGS, type Backend } from "./storage";
import type { Workout } from "./workout";

/** Record id in META. */
export const REP_SENSOR = "repSensor";

/** Workout sensor sets kept per exercise (owner 2026-10-31: a limit per exercise); recordings made on purpose stay. */
export const WORKOUT_RECORDINGS_PER_EXERCISE = 20;

/** Where the phone is during a workout's sensor sets (question Q2's default; the Zähltest lets the owner pick). */
export const WORKOUT_PLACEMENT: Placement = "pocket";

export interface ExerciseSensor {
  /** "Reps per Sensor zählen" is on for the exercise's timed sets */
  on?: boolean;
  profiles?: Partial<Record<Placement, RepProfile>>;
}

export interface RepSensorSettings {
  id: typeof REP_SENSOR;
  exercises: Record<string, ExerciseSensor>;
}

const empty = (): RepSensorSettings => ({ id: REP_SENSOR, exercises: {} });

export async function loadRepSensor(db: Backend): Promise<RepSensorSettings> {
  const r = await db.get<RepSensorSettings>(META, REP_SENSOR);
  return r && typeof r.exercises === "object" && r.exercises ? r : empty();
}

export const saveRepSensor = (db: Backend, s: RepSensorSettings) => db.put(META, s);

export const sensorOn = (s: RepSensorSettings, exerciseId: string) => !!s.exercises[exerciseId]?.on;

export const profileOf = (s: RepSensorSettings, exerciseId: string, placement: Placement): RepProfile | undefined =>
  s.exercises[exerciseId]?.profiles?.[placement];

export function withSensorOn(s: RepSensorSettings, exerciseId: string, on: boolean): RepSensorSettings {
  return { ...s, exercises: { ...s.exercises, [exerciseId]: { ...s.exercises[exerciseId], on } } };
}

/** The settings with the exercise's profile for the placement refined by a set of `reps` reps (as confirmed). */
export function withLearned(s: RepSensorSettings, r: Pick<MotionRecording, "exercise" | "placement" | "accel" | "setStart" | "setEnd">, reps: number): RepSensorSettings {
  const id = r.exercise.id;
  const before = profileOf(s, id, r.placement);
  const after = learnProfile(r.accel, r.setStart, r.setEnd, reps, before);
  if (!after || after === before) return s;
  const x = s.exercises[id];
  return { ...s, exercises: { ...s.exercises, [id]: { ...x, profiles: { ...x?.profiles, [r.placement]: after } } } };
}

/** Stores the recording of a sensor set (or of the Zähltest) and returns its id. */
export async function storeSensorRecording(db: Backend, raw: RawRecording, info: RecordingInfo): Promise<string> {
  const id = newId();
  const rec: StoredRecording = { id, ...buildRecording(raw, info) };
  await db.put(RECORDINGS, rec);
  return id;
}

/**
 * The workout ends (finished or discarded): every ticked sensor set's recording gets the reps as they stand now
 * (corrected in the row or not) as its ground truth, and the exercise's profile learns from it. The recordings of
 * sets that were not ticked (they are dropped), and all of a discarded workout, are deleted: nobody confirmed their
 * reps. Then only the newest `WORKOUT_RECORDINGS_PER_EXERCISE` workout recordings of each exercise used stay.
 * Returns the settings with the refined profiles (already saved).
 */
export async function settleSensorSets(db: Backend, w: Workout, kept: boolean): Promise<RepSensorSettings> {
  let settings = await loadRepSensor(db);
  let learned = false;
  for (const x of w.exercises) {
    for (const s of x.sets) {
      if (!s.sensor) continue;
      const rec = await db.get<StoredRecording>(RECORDINGS, s.sensor.recordingId);
      if (!rec) continue;
      if (!kept || !s.done || s.reps === undefined) {
        await db.delete(RECORDINGS, rec.id);
        continue;
      }
      const confirmed: StoredRecording = { ...rec, reps: s.reps };
      await db.put(RECORDINGS, confirmed);
      settings = withLearned(settings, rec, s.reps);
      learned = true;
    }
  }
  if (learned) {
    await saveRepSensor(db, settings);
    await trimWorkoutRecordings(db, new Set(w.exercises.map((x) => x.exerciseId)));
  }
  return settings;
}

/** Deletes the oldest workout recordings of the exercises beyond the limit (recording screen and Zähltest: never). */
export async function trimWorkoutRecordings(db: Backend, exerciseIds: Set<string>, keep = WORKOUT_RECORDINGS_PER_EXERCISE) {
  const all = (await db.getAll<StoredRecording>(RECORDINGS)).filter((r) => r.origin === "workout" && exerciseIds.has(r.exercise.id));
  for (const id of exerciseIds) {
    const old = all.filter((r) => r.exercise.id === id).sort((a, b) => b.recordedAt - a.recordedAt).slice(keep);
    for (const r of old) await db.delete(RECORDINGS, r.id);
  }
}
